/**
 * useRoundedBarChart - Cria gráficos de barra com topo arredondado
 * 
 * Usa o tipo 'bar' padrão do Chart.js com modificação do prototype do Rectangle
 * para desenhar topos arredondados. Compatível com Vite/HMR.
 */

declare global {
  interface Window {
    Chart: any;
  }
}

/**
 * Modifica o prototype do Rectangle para desenhar topos arredondados
 * Esta abordagem funciona melhor com Vite/HMR do que registrar um novo tipo
 */
function enableRoundedBars(): void {
  const Chart = window.Chart;
  if (!Chart || !Chart.elements || !Chart.elements.Rectangle) return;
  
  // Se já foi modificado, não faz nada
  if ((Chart.elements.Rectangle.prototype as any)._roundedBarPatched) return;

  // Guarda o draw original
  const originalDraw = Chart.elements.Rectangle.prototype.draw;

  // Substitui o draw para desenhar com topo arredondado
  Chart.elements.Rectangle.prototype.draw = function() {
    const ctx = this._chart.ctx;
    const vm = this._view;
    
    // Verifica se barRoundness está habilitado nas opções do gráfico
    const barRoundness = this._chart.config.options?.barRoundness;
    
    // Se não tem barRoundness, usa draw original
    if (!barRoundness) {
      return originalDraw.call(this);
    }

    let left: number, right: number, top: number, bottom: number;
    const borderWidth = vm.borderWidth || 0;

    if (!vm.horizontal) {
      left = vm.x - vm.width / 2;
      right = vm.x + vm.width / 2;
      top = vm.y;
      bottom = vm.base;
    } else {
      left = vm.base;
      right = vm.x;
      top = vm.y - vm.height / 2;
      bottom = vm.y + vm.height / 2;
    }

    // Calcula o raio baseado na largura da barra
    const barWidth = Math.abs(right - left);
    const barHeight = Math.abs(bottom - top);
    
    // Se a barra é muito pequena, usa draw original
    if (barWidth < 2 || barHeight < 2) {
      return originalDraw.call(this);
    }

    // Raio mais generoso para barras mais arredondadas (como no Rails original)
    // barRoundness = 1 significa raio = metade da largura da barra
    const radius = Math.min(barWidth * barRoundness * 0.5, barHeight * 0.4, barWidth * 0.5);

    ctx.beginPath();
    ctx.fillStyle = vm.backgroundColor;
    ctx.strokeStyle = vm.borderColor;
    ctx.lineWidth = borderWidth;

    // Desenha retângulo com topo arredondado
    if (top < bottom) {
      // Barra para cima (valores positivos)
      ctx.moveTo(left + radius, top);
      ctx.lineTo(right - radius, top);
      ctx.quadraticCurveTo(right, top, right, top + radius);
      ctx.lineTo(right, bottom);
      ctx.lineTo(left, bottom);
      ctx.lineTo(left, top + radius);
      ctx.quadraticCurveTo(left, top, left + radius, top);
    } else {
      // Barra para baixo (valores negativos)
      ctx.moveTo(left, top);
      ctx.lineTo(right, top);
      ctx.lineTo(right, bottom + radius);
      ctx.quadraticCurveTo(right, bottom, right - radius, bottom);
      ctx.lineTo(left + radius, bottom);
      ctx.quadraticCurveTo(left, bottom, left, bottom + radius);
      ctx.lineTo(left, top);
    }

    ctx.closePath();
    ctx.fill();
    
    if (borderWidth) {
      ctx.stroke();
    }
  };

  // Marca como modificado para não aplicar novamente
  (Chart.elements.Rectangle.prototype as any)._roundedBarPatched = true;
}

export interface RoundedBarChartOptions {
  labels: (string | number)[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor?: string;
    borderWidth?: number;
  }[];
  tooltipUnit?: string;
  yAxisLabel?: string;
  xAxisLabel?: string;
  barRoundness?: number;
  maintainAspectRatio?: boolean;
  paddingTop?: number;
  delayedFill?: number; // ms para delay antes de preencher dados (default: 100)
}

/**
 * Função helper para criar gráfico de barras arredondadas
 * Usa type: 'bar' com modificação do prototype para bordas arredondadas
 */
export function createRoundedBarChart(
  canvas: HTMLCanvasElement,
  options: RoundedBarChartOptions
): any {
  const Chart = window.Chart;
  if (!Chart) return null;

  // Habilita barras arredondadas (modifica prototype)
  enableRoundedBars();

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Guarda os dados reais para preencher depois
  const realData = options.datasets.map(ds => [...ds.data]);
  const delayMs = options.delayedFill ?? 400;

  // Cria com dados zerados para animar o preenchimento
  const chartData = {
    labels: options.labels,
    datasets: options.datasets.map((ds, i) => ({
      ...ds,
      borderWidth: ds.borderWidth ?? 0,
      data: realData[i].map(() => 0), // Começa com zeros
    })),
  };

  // Usa type: 'bar' padrão - as bordas arredondadas são aplicadas via prototype
  const chart = new Chart(ctx, {
    type: 'bar',
    data: chartData,
    options: {
      responsive: true,
      maintainAspectRatio: options.maintainAspectRatio !== false,
      barRoundness: options.barRoundness ?? 1,
      animation: {
        duration: 400,
        easing: 'easeOutQuart',
      },
      layout: { padding: { top: options.paddingTop ?? 20, bottom: 10 } },
      legend: {
        display: false,
        position: 'top',
        labels: { boxWidth: 5, usePointStyle: true }
      },
      tooltips: {
        position: 'nearest',
        mode: 'index',
        intersect: true,
        borderWidth: 1,
        borderColor: '#7b8aa4',
        backgroundColor: 'rgba(255,255,255,1)',
        bodyFontStyle: 'normal',
        titleFontStyle: 'normal',
        titleFontColor: '#7b8aa4',
        bodyFontColor: '#7b8aa4',
        caretPadding: 10,
        caretSize: 5,
        callbacks: {
          label: function(tooltipItems: any, chartDataRef: any) {
            const index = tooltipItems.datasetIndex;
            const label = chartDataRef.datasets[index].label;
            const value = tooltipItems.yLabel;
            const unit = options.tooltipUnit || '';
            return ' ' + label + ': ' + value + (unit ? ' ' + unit : '');
          }
        }
      },
      hover: { mode: 'point', intersect: true },
      scales: {
        yAxes: [{
          display: true,
          scaleLabel: {
            display: !!options.yAxisLabel,
            labelString: options.yAxisLabel || '',
            fontStyle: 'normal',
            padding: 5
          },
          gridLines: {
            display: true,
            color: 'rgba(187, 186, 186, 0.2)',
            zeroLineColor: '#f0f0f0',
            zeroLineWidth: 1,
            drawBorder: false
          },
          ticks: {
            beginAtZero: true,
            fontFamily: "'Poppins', sans-serif",
            fontColor: '#bbbaba'
          },
          stacked: false
        }],
        xAxes: [{
          display: true,
          scaleLabel: {
            display: !!options.xAxisLabel,
            labelString: options.xAxisLabel || '',
            fontStyle: 'normal',
            padding: 5
          },
          gridLines: { display: false },
          ticks: {
            fontFamily: "'Poppins', sans-serif",
            fontColor: '#bbbaba'
          },
          stacked: false
        }],
      }
    }
  });

  // Após delay, preenche com os dados reais (animação de preenchimento)
  setTimeout(() => {
    if (chart && chart.data && chart.data.datasets) {
      chart.data.datasets.forEach((ds: any, i: number) => {
        ds.data = realData[i];
      });
      chart.update();
    }
  }, delayMs);

  return chart;
}

// Exporta a função de habilitar barras arredondadas para uso manual
export { enableRoundedBars };
